/**
 * T307 · Pre-replace cleanup on JWT swap.
 *
 * Spec 05 §683 + §1283-1289. The cleanup sequence run when swapping JWT
 * identity (login: anon → user; logout: user → anon). Closing the NATS
 * connection alone is INSUFFICIENT — the OLD `Map<Element, Acquisition>`
 * still GC-roots `Acquisition` objects whose `release` closures
 * reference the OLD module's `callbacks` map and `natsClient.subscribe`
 * binding. Two parallel sets of subscribers would coexist on the same
 * WS.
 *
 * Order (verbatim from file 05 §1284-1289):
 *   1. cancelRefresh — stop the pre-expiry timer before anything else.
 *   2. iterate `__morfi_registry_observer.elementMap.entries()` and
 *      call `acquisition.release()` on each.
 *   3. observer.disconnect()
 *   4. elementMap.clear()
 *   5. iterate subjectCallbacks + clear (via registry.destroy())
 *   6. clearInterval(sweepHandle) — already done as part of step 1 of
 *      `teardownOldRegistry`.
 *   7. drain + reset connection slot
 *   8. jwtStore.clear()
 *   9. (logout only) `queryClient.removeQueries({ predicate: q =>
 *      q.queryKey[0] === 'user' || q.queryKey[0] === 'me' })`
 *
 * After cleanup, `acquireConnection(config)` rebuilds via the cold path
 * with the new JWT. The mutex is reset to STABLE so post-cleanup
 * remint can proceed.
 */

import { cancelColdConnectRetry } from '../connect/cold-connect-retry.js';
import { __resetConnectionSingletonForTests } from '../connect/connect.js';
import { teardownOldRegistry } from '../connect/hmr-cleanup.js';
import { markIntentionalClose } from '../connect/intentional-close.js';
import { freezePinnedFilterManager } from '../wire/filter-manager.js';
import { __resetJwtStoreForTests } from './jwt-store.js';
import { _resetConnectionActionForTest } from './reconnect-mutex.js';
import { cancelRefresh } from './refresh-ladder.js';

/**
 * Global slot for a `reinstallVisibility` thunk registered by the active
 * platform adapter (web sets it via `installWebVisibility`; React Native
 * registers its always-visible, virtualization-owned controller).
 *
 * Called from {@link preReplaceCleanup} AFTER `teardownOldRegistry()`
 * wipes the registry singleton, BEFORE `connectUnderIdentity` opens the
 * new connection. Without this re-install, the fresh registry has no
 * `VisibilityController` → `useItemObserver` returns no-op refs →
 * per-row wire subjects never register → realtime silently dies after
 * any identity flip.
 */
const VISIBILITY_REINSTALL_KEY = '__morfi_visibility_reinstall_thunk__' as const;
type GlobalWithReinstaller = typeof globalThis & {
  [VISIBILITY_REINSTALL_KEY]?: () => void;
};

function runVisibilityReinstall(): void {
  try {
    (globalThis as GlobalWithReinstaller)[VISIBILITY_REINSTALL_KEY]?.();
  } catch {
    // Defensive — never block the cleanup chain on a visibility install
    // failure; the next render will still mount whatever the adapter
    // re-registers on first useEffect.
  }
}

/** Identity transition that motivated the cleanup. */
export type JwtSwapKind = 'login' | 'logout';

/**
 * Minimal QueryClient shape — we depend on `removeQueries` only. Typed
 * structurally so this module does not pull `@tanstack/react-query` into
 * the import graph (keeps the `/client` barrel tree-shakable).
 */
export interface QueryClientLike {
  removeQueries(filters: { predicate(query: { queryKey: readonly unknown[] }): boolean }): void;
}

/** Minimal NatsConnection shape — we only need `drain()`. */
export interface NatsConnectionLike {
  drain(): Promise<void>;
}

export interface PreReplaceCleanupDeps {
  /** The transitioning identity kind — `'logout'` triggers query removal. */
  readonly kind: JwtSwapKind;
  /**
   * The live nats connection to drain. Pass `null` if no connection
   * was established (e.g. mint failed on first boot).
   */
  readonly nc: NatsConnectionLike | null;
  /** QueryClient — required on logout to drop user-scoped query data. */
  readonly queryClient?: QueryClientLike;
}

/**
 * Run the cleanup sequence. Returns once the slot has been reset; the
 * caller then runs the bootstrap mint + acquireConnection path against
 * the new identity.
 *
 * Each step is wrapped in try/catch so a failure in one stage does not
 * leak partial state into the next — the goal is "after this returns,
 * the global slots are sane regardless of upstream state".
 */
export async function preReplaceCleanup(deps: PreReplaceCleanupDeps): Promise<void> {
  // 1. Cancel the pre-expiry timer FIRST so a late-firing refresh can't
  //    re-arm against a JWT that's about to be cleared.
  try {
    cancelRefresh();
  } catch {
    // Idempotent; should never throw — but stay defensive.
  }

  // 1.1. Drop any pending cold-connect rung: it would reconnect under the
  //      identity we are about to leave. The new identity's connect arms
  //      its own ladder if it needs one.
  try {
    cancelColdConnectRetry();
  } catch {
    // Idempotent.
  }

  // 1.5. Freeze the live wire FilterManager BEFORE releasing interest or
  //      draining the connection. `teardownOldRegistry` (next) releases every
  //      Acquisition, each of which schedules a debounced filter-subjects
  //      flush; the drain below (step 7) then closes the connection. Without
  //      this freeze, the debounced flush fires `consumers.update` against the
  //      now-closed connection → a caught-but-noisy `ClosedConnectionError` on
  //      every login/logout. Freeze sets `destroyed` (so the scheduled flush
  //      bails) and cancels the debounce timer. The manager's full teardown
  //      (consumer delete) still runs via the subscription-effect cleanup.
  try {
    freezePinnedFilterManager();
  } catch {
    // Defensive — never block the cleanup chain on the freeze.
  }

  // 2-6. Release every observed Acquisition, disconnect the observer,
  //      clear the elementMap, destroy the registry. `teardownOldRegistry`
  //      owns the exact order from §1283.
  try {
    teardownOldRegistry();
  } catch {
    // Tolerate teardown throws — first construction will set fresh
    // instances regardless.
  }

  // 2a. Re-install the platform's VisibilityController on the freshly-
  //     constructed registry. `installWebVisibility` (or the RN
  //     equivalent) ran at SDK mount and captured an install thunk on
  //     `globalThis`. Calling it now ensures `useItemObserver` sees
  //     `hasVisibility() === true` after the identity flip — otherwise
  //     per-row wire subjects never register and realtime appears dead
  //     after login/logout.
  runVisibilityReinstall();

  // 7. Drain the live connection (preferred over `close()` so the
  //    broker reclaims SUB slot accounting cleanly — see file 05
  //    §2883 RN deliverable 1). drain() failure is caught and
  //    non-fatal — the slot reset still wipes the dead reference.
  if (deps.nc !== null) {
    // Ours — the `closed()` watcher must not treat this drain as the broker
    // giving up and start a rebuild behind the flip.
    markIntentionalClose(deps.nc);
    try {
      await deps.nc.drain();
    } catch {
      // Drain on an already-closed connection rejects — the slot reset
      // below wipes the reference regardless.
    }
  }
  // Slot reset — drops `__morfi_connection_promise__` so the next
  // `connectSingleton` call enters the cold path.
  __resetConnectionSingletonForTests();

  // 8. Clear the JWT cell. The next mint writes a fresh one before the
  //    authenticator closure is invoked again.
  __resetJwtStoreForTests();

  // 9. (logout only) drop ALL user-scoped query data. The predicate
  //    matches by `queryKey[0]` — NOT a literal-string scan for
  //    `'userId'`, which would miss every key per file 05 §683.
  if (deps.kind === 'logout' && deps.queryClient !== undefined) {
    deps.queryClient.removeQueries({
      predicate: (q) => {
        const head = q.queryKey[0];
        return head === 'user' || head === 'me';
      },
    });
  }

  // Reset the connection-action state machine to STABLE so the
  // post-cleanup remint can acquire fresh.
  _resetConnectionActionForTest();
}
